/**
 * Prepare-step primitive, extracted from the original `PrepareStep` body: the stakeholder roster, a
 * self-contained section (its own `Block` wrapper) so a `step_layout` can order, drop, or repeat it.
 *
 * Section title/lead/icon come from the primitive's authored `config`, falling back to the resolved
 * `sectionHeadings` — the same source the original body read — while content data comes from the shared
 * `RunnerContext` plus the existing DB resolvers. No presentation is duplicated: this reuses the same
 * `Block` leaf the body used.
 */
import { resolveStakeholders } from '../../api';
import { resolveStakeholderPriority } from '../../api/resolveContent';
import { Block, cx } from '../../components/ui/primitives';
import { asIconName } from '../../components/ui/Icon';
import { sectionHeadings } from '../../config/sectionHeadings';
import s from '../../components/steps/steps.module.css';
import type { PrimitiveProps } from './registry';

export function StakeholderRoster({ config }: PrimitiveProps): JSX.Element {
  const stakeholderPriority = resolveStakeholderPriority();
  const h = sectionHeadings('prepare');
  const title = (config?.title as string) || h.roster?.title || '';
  const lead = (config?.lead as string) || h.roster?.lead;

  return (
    <Block icon={asIconName(config?.icon, 'people')} title={title} lead={lead}>
      <ul className={s.roleList}>
        {resolveStakeholders().map((person) => (
          <li key={person.label}>
            <div className={s.roleHead}>
              <span className={s.roleLabel}>{person.label}</span>
              <span className={cx(s.roleTag, s[`role_${person.role}`])}>
                {stakeholderPriority[person.role]}
              </span>
            </div>
            <p className={s.roleWhy}>{person.why}</p>
          </li>
        ))}
      </ul>
    </Block>
  );
}
